/**
 * The web search field (spec 14.1, 14.2, 14.8, 14.10, 14.11).
 *
 * Why this exists rather than a wrapper around the shared component:
 *
 * zen-core's ZenSearchBar destructures a fixed prop list and forwards nothing
 * else, so there is no way to put a marker attribute on the box it renders -
 * and the marker is the whole fix. The focus ring has to land on the bordered
 * container, not on the borderless <input> sitting inside it, otherwise the
 * browser paints a second rectangle a few pixels in from the rounded border.
 * That inset rectangle is the "extra focus border" reported on the Sell
 * Screen, Brand List, Product List and Create Customer searches.
 *
 * The public API is ZenSearchBar's, unchanged and fully implemented, so every
 * existing call site keeps working untouched. Only presentation differs, and
 * only on web: Metro never resolves this file, so Android keeps the original
 * component exactly as it is.
 *
 * Sizing comes from webTokens.webSearch, which reuses the control tokens, so
 * a search field, a text input and an `md` button in the same toolbar row
 * finally share a height and a radius (14.11).
 */

import React, {FC} from 'react';
import {StyleSheet, TextInput, TouchableOpacity, View} from 'react-native';

import ZenIcon from 'zen-core-elements/src/elements/zenIcon/zenIcon';
import {ZenSearchBarTypes} from 'zen-core-elements/src/elements/zenSearchBar/zenSearchBar';
import {appColors} from 'zen-core-elements/src/shared/constants';

import {webFontFamily, webSearch} from './webTokens';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: webSearch.gap,
    backgroundColor: appColors.light,
    borderWidth: webSearch.borderWidth,
    borderColor: appColors.borderColor,
    borderRadius: webSearch.borderRadius,
    paddingHorizontal: webSearch.paddingHorizontal,
    height: webSearch.height,
  },
  /**
   * No `top` offset. The native component nudges its text up by 3px to
   * compensate for how Android centres a TextInput's line box; on web the
   * flex container already centres it, so the same offset just prints the
   * text above the middle of the box.
   */
  input: {
    flex: 1,
    color: appColors.lightDark,
    fontSize: webSearch.fontSize,
    fontFamily: webFontFamily.regular,
    /*
     * The <input> fills the box and is never the thing that shows a border:
     * outlineWidth 0 keeps react-native-web from emitting one, and the ring
     * is painted on the container by globalStyles.web.ts instead.
     */
    borderWidth: 0,
    outlineWidth: 0,
    paddingVertical: 0,
  },
});

/**
 * `dataSet` renders data-* attributes on web. react-native's prop types do
 * not declare it, so each use needs a cast; both markers below are read by
 * globalStyles.web.ts and by nothing else.
 */
const BOX_MARKER = {dataSet: {zenFieldBox: 'search'}} as object;
const FIELD_MARKER = {dataSet: {zenField: 'true'}} as object;

const WebZenSearchBar: FC<ZenSearchBarTypes> = ({
  search,
  searchContainerStyle,
  searchInputStyle,
  placeholder,
  onCancel,
  clearIcon,
  searchIcon,
  disabled,
  leftIconContainerStyle,
  rightIconContainerStyle,
  placeholderTextColor,
  testID,
  showLeftIcon = true,
  setSearch,
}) => (
  <View {...BOX_MARKER} style={[styles.container, searchContainerStyle]}>
    {searchIcon || showLeftIcon ? (
      <View style={leftIconContainerStyle}>
        <ZenIcon
          type={searchIcon?.type ? searchIcon.type : 'material'}
          name={searchIcon?.name ? searchIcon.name : 'search'}
          size={searchIcon?.size ? searchIcon.size : webSearch.iconSize}
          color={searchIcon?.color ? searchIcon.color : appColors.grey}
        />
      </View>
    ) : undefined}

    <TextInput
      {...FIELD_MARKER}
      editable={!disabled}
      selectTextOnFocus={false}
      testID={testID}
      style={[styles.input, searchInputStyle]}
      placeholder={placeholder ? placeholder : 'Search...'}
      placeholderTextColor={
        placeholderTextColor ? placeholderTextColor : appColors.placeholderColor
      }
      value={search}
      onChangeText={searchText => {
        setSearch(searchText);
      }}
      returnKeyType="search"
    />

    {search ? (
      <TouchableOpacity
        style={rightIconContainerStyle}
        onPress={() => {
          if (onCancel) {
            onCancel();
          } else {
            setSearch('');
          }
        }}
      >
        <ZenIcon
          name={clearIcon?.name ? clearIcon.name : 'clear'}
          type={clearIcon?.type ? clearIcon.type : 'material'}
          size={clearIcon?.size ? clearIcon.size : webSearch.iconSize}
          color={clearIcon?.color ? clearIcon.color : appColors.placeholderColor}
        />
      </TouchableOpacity>
    ) : null}
  </View>
);

export default WebZenSearchBar;
